"use client";

import clsx from "clsx";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/hooks/useAlert";
import {
  FileUploadConfig,
  getDefaultFileCount,
  getDefaultFileSize,
  validateFilesWithFeedbackAsync,
} from "@/utils/fileHelpers";
import Icon from "../library/Icon";

interface FileDropZoneProps {
  children: ReactNode;
  onFilesSelected: (files: File[]) => Promise<void> | void;
  acceptedFileTypes?: string;
  allowMultiple?: boolean;
  disabled?: boolean;
  className?: string;
  fileUploadConfig?: FileUploadConfig;
  assistantModel?: string; // New prop for PDF page validation
  onInvalidFiles?: (rejectedFiles: { file: File; reason: string }[]) => void;
  showAlerts?: boolean; // New prop to enable specific alert messages
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  children,
  onFilesSelected,
  acceptedFileTypes = "*/*",
  disabled = false,
  className,
  fileUploadConfig,
  assistantModel,
  onInvalidFiles,
  showAlerts = false,
}) => {
  const { t } = useTranslation("ask");
  const { setAlert } = useAlert();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const dragCounterRef = useRef(0);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Helper function to generate readable file types from accept string
  const generateSupportedFormatsText = (): string => {
    const config = fileUploadConfig;

    // If no config provided, fall back to original translation
    if (!config) {
      return t("supportedFormats", {
        defaultValue: "Supported formats: Images, PDF, DOC, TXT, MD",
      });
    }

    if (!config.enabled) {
      return t("fileUploadDisabled", {
        defaultValue: "File uploads are not available for this assistant",
      });
    }

    const acceptTypes = config.acceptedFileTypes ?? acceptedFileTypes;
    const maxCount = config.maxFileCount ?? 1;

    // Parse accepted file types
    const types = acceptTypes.split(",").map((type) => type.trim());
    const readableTypes: string[] = [];

    types.forEach((type) => {
      if (type === "image/*") {
        readableTypes.push(t("fileTypes.images", { defaultValue: "Images" }));
      } else if (type === ".pdf" || type === "application/pdf") {
        readableTypes.push("PDF");
      } else if (
        type === ".docx" ||
        type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        readableTypes.push("DOCX");
      } else if (type === ".txt" || type === "text/plain") {
        readableTypes.push("TXT");
      } else if (type === ".md" || type === "text/markdown") {
        readableTypes.push("MD");
      } else if (type.startsWith(".")) {
        readableTypes.push(type.substring(1).toUpperCase());
      }
    });

    const typesText =
      readableTypes.length > 0
        ? readableTypes.join(", ")
        : t("fileTypes.allFiles", { defaultValue: "All files" });

    const countText =
      maxCount > 1
        ? ` (${t("maxFiles", {
            count: maxCount,
            defaultValue: `max ${maxCount} files`,
          })})`
        : "";

    const prefixText = t("supportedFormatsPrefix", {
      defaultValue: "Supported formats",
    });

    return `${prefixText}: ${typesText}${countText}`;
  };

  const validateFiles = async (files: File[]): Promise<File[]> => {
    // Build unified config for validation. The caller (useChat) is responsible
    // for applying GailGPT file-size overrides via applyGailGptLimits — we
    // don't re-apply here to keep a single application point (DRY).
    const unifiedConfig = fileUploadConfig
      ? {
          ...fileUploadConfig,
          enabled: true, // Always treat as enabled for validation
          acceptedFileTypes:
            fileUploadConfig.acceptedFileTypes || acceptedFileTypes,
        }
      : {
          enabled: true,
          acceptedFileTypes,
          maxFileSize: getDefaultFileSize(),
          maxFileCount: getDefaultFileCount(),
          totalMaxFileSize: getDefaultFileSize(),
        };

    const { validFiles, rejectedFiles } = await validateFilesWithFeedbackAsync(
      files,
      unifiedConfig,
      assistantModel,
      {
        showAlerts,
        setAlert,
        t,
      }
    );

    // Call the callback with rejected files if any, but only if we haven't shown specific alerts
    // This prevents duplicate/conflicting alert messages
    if (rejectedFiles.length > 0 && onInvalidFiles && !showAlerts) {
      onInvalidFiles(rejectedFiles);
    }

    return validFiles;
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    const fileArray = Array.from(files);
    const hasPdf = fileArray.some((f) => f.type === "application/pdf");

    if (hasPdf) setIsValidating(true);
    try {
      const validFiles = await validateFiles(fileArray);
      if (validFiles.length > 0) {
        await onFilesSelected(validFiles);
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Handle drag events that might escape our container
  const handleWindowDragLeave = (e: DragEvent) => {
    // If the drag leaves the window entirely, reset our state
    if (!e.relatedTarget) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  };

  // Drag handlers are attached imperatively so the drop zone container stays
  // a non-interactive landmark in JSX (jsx-a11y/no-static-element-interactions).
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current++;

      if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current--;

      if (dragCounterRef.current === 0) {
        setIsDragOver(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (disabledRef.current) return;

      void handleFilesRef.current(e.dataTransfer?.files ?? null);
    };

    dropZone.addEventListener("dragenter", handleDragEnter);
    dropZone.addEventListener("dragleave", handleDragLeave);
    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("drop", handleDrop);

    return () => {
      dropZone.removeEventListener("dragenter", handleDragEnter);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("dragover", handleDragOver);
      dropZone.removeEventListener("drop", handleDrop);
    };
  }, []);

  // Add window event listeners to handle edge cases
  useEffect(() => {
    const handleWindowDragEnd = () => {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    };

    window.addEventListener("dragend", handleWindowDragEnd);
    window.addEventListener("dragleave", handleWindowDragLeave);

    return () => {
      window.removeEventListener("dragend", handleWindowDragEnd);
      window.removeEventListener("dragleave", handleWindowDragLeave);
    };
  }, []);

  return (
    <div
      ref={dropZoneRef}
      role="region"
      aria-label={t("fileDropZone")}
      className={clsx("relative", className)}
    >
      {children}

      {/* Drag overlay */}
      {isDragOver && !disabled && (
        <div className="z-50 absolute inset-0 flex justify-center items-center bg-gailPrimary bg-opacity-40 border-2 border-gailPrimary border-dashed rounded-lg pointer-events-none">
          <div className="bg-panel shadow-lg p-8 rounded-lg">
            <div className="flex items-center gap-3 font-medium text-gailPrimary text-xl">
              <Icon icon="upload" />
              {t("dropFilesHereToAttach")}
            </div>
            <p className="mt-2 text-600 text-sm">
              {generateSupportedFormatsText()}
            </p>
          </div>
        </div>
      )}

      {/* Validating overlay — shown while PDF pages are being counted */}
      {isValidating && (
        <div className="z-50 absolute inset-0 flex justify-center items-center bg-panel/60 pointer-events-none">
          <div className="bg-panel shadow-lg px-6 py-4 rounded-lg flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-gailPrimary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-600">{t("thinking")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
